import React, { useState } from 'react';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { generateDescription } from '../../config';

export default function AIDescriptionPanel({ productData, currentDescription, onDescriptionAccepted }) {
  const [aiDescription, setAiDescription] = useState(null);
  const [editedOriginal, setEditedOriginal] = useState(null);
  const [editedAi, setEditedAi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { name, condition, processor, ram, storage, screen_size, price, description } = productData;
      const res = await generateDescription({ name, condition, processor, ram, storage, screen_size, price: price ? parseFloat(price) : null, description });
      const desc = Array.isArray(res)
        ? res[0]?.output || res[0]?.description_commerciale || ''
        : res?.description_commerciale || res?.output || '';
      setAiDescription(desc);
      setEditedAi(desc);
      setEditedOriginal(currentDescription || '');
    } catch (err) {
      setError(err.message || 'Erreur lors de la generation de la description.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeep = (desc) => {
    onDescriptionAccepted(desc);
    setAiDescription(null);
    setEditedAi(null);
    setEditedOriginal(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 bg-slate-50 rounded-sm border border-slate-200">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        <p className="text-slate-700 text-sm">L'IA rédige votre description commerciale…</p>
        <p className="text-slate-500 text-xs">Cela peut prendre jusqu'à 1 minute</p>
      </div>
    );
  }

  // Result state — side-by-side editable comparison
  if (aiDescription) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Original — editable (slate clair lisible) */}
          <div className="bg-slate-50 rounded-sm border border-slate-200 p-4 flex flex-col">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Description actuelle</h4>
            <textarea
              value={editedOriginal}
              onChange={e => setEditedOriginal(e.target.value)}
              rows={8}
              className="flex-1 bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm text-primary outline-none resize-none focus:border-secondary whitespace-pre-line"
            />
            <button
              onClick={() => handleKeep(editedOriginal)}
              className="mt-3 w-full py-2 px-4 rounded-sm text-sm font-medium bg-slate-200 hover:bg-slate-300 text-primary transition-colors"
            >
              Garder cette version
            </button>
          </div>

          {/* AI version — editable (violet doux) */}
          <div className="rounded-sm p-4 flex flex-col bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-300">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-700 flex items-center gap-1 mb-2">
              <Sparkles className="w-4 h-4" /> Description IA
            </h4>
            <textarea
              value={editedAi}
              onChange={e => setEditedAi(e.target.value)}
              rows={8}
              className="flex-1 bg-white border border-violet-200 rounded-sm px-3 py-2 text-sm text-primary outline-none resize-none focus:border-violet-500 whitespace-pre-line"
            />
            <button
              onClick={() => handleKeep(editedAi)}
              className="mt-3 w-full py-2 px-4 rounded-sm text-sm font-medium text-white transition-colors bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              Garder cette version
            </button>
          </div>
        </div>

        {/* Regenerate + tip */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleGenerate}
            className="flex items-center gap-2 py-2 px-4 rounded-sm text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Régénérer
          </button>
          <p className="text-xs text-slate-500">
            Modifiez directement le texte dans chaque zone, puis cliquez "Garder" pour valider.
          </p>
        </div>
      </div>
    );
  }

  // Initial state
  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-sm bg-red-50 border border-red-300 text-red-800 text-sm">
          {error}
        </div>
      )}

      {currentDescription && (
        <div className="bg-slate-50 rounded-sm border border-slate-200 p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Description actuelle</h4>
          <p className="text-primary text-sm whitespace-pre-line leading-relaxed">{currentDescription}</p>
        </div>
      )}

      <button
        onClick={handleGenerate}
        className="flex items-center gap-2 py-2.5 px-5 rounded-sm text-sm font-medium text-white transition-colors bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
      >
        <Sparkles className="w-4 h-4" /> Générer description IA
      </button>
    </div>
  );
}
